import React, {
	FC, useEffect, useMemo, useRef,
} from 'react';

import 'react-toastify/dist/ReactToastify.css';
import dayjs from 'dayjs';
import en from 'dayjs/locale/en';
import {
	alephToast,
	NotificationToast,
	NotificationType,
	RequestPasswordResetPage,
	PasswordResetPage,
	LoginBlock,
	AdBlockModal,
} from '@aleph/shared-components';
import SpotifyResponse from 'wb-spotify/app/routes/superGrid/tool/advertiserOAuth/spotifyResponse';
import AppRoutes from 'wb-spotify/app/routes/index';
import * as Sentry from '@sentry/react';
import AuthProvider from 'auth/lib/provider';
import useAccessTokenStore from 'auth/lib/useAccessTokenStore';
import logoImg from 'wb-reddit/assets/logo-wiseblue.svg';
import RedditSideMenu from 'wb-reddit/app/common/components/sideMenu';
import { getPublicClient, NETWORK, REFRESH_TOKEN_URI } from 'wb-spotify/app/common/utils';
import { RequestResetPasswordDocument, ResetPasswordDocument } from 'wb-reddit/graphql/generated';
import { useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import useSupergridSettings from 'wb-spotify/app/routes/superGrid/hooks/useSupergridSettings';
import useConfirmActionDialog from 'wb-reddit/app/routes/hooks/useConfirmAction';
import ConfirmLogoutDialog from 'wb-spotify/app/common/components/confirmLogoutDialog';

const IGNORED_ERROR_MESSAGES = [
	'ResizeObserver loop limit exceeded',
	'ResizeObserver loop completed with undelivered notifications.',
];

export const onSessionExpired = () => {
	alephToast(
		({ closeToast }) => (
			<NotificationToast
				closeToast={closeToast}
				type={NotificationType.Warning}
				title={<div>Session Expired!</div>}
				description={<div>You have been logged out due to inactivity!</div>}
			/>
		),
		{ position: 'top-center' },
	);
};

const App: FC = () => {
	const logoutRef = useRef<(() => Promise<void>) | null>(null);

	const publicClient = useMemo(() => getPublicClient({
		uri: `${import.meta.env.VITE_API_PATH}/graphql/public/${NETWORK}/`,
	}), []);

	const showRequestPasswordReset = useMatch('/requestPasswordReset');
	const showResetPassword = useMatch('/resetPassword');

	const navigate = useNavigate();
	const [urlParams] = useSearchParams();

	useEffect(() => {
		const ignoreErrors = (e) => {
			if (IGNORED_ERROR_MESSAGES.includes(e.message)) {
				const resizeObserverErrDiv = document.getElementById(
					'webpack-dev-server-client-overlay-div',
				);
				const resizeObserverErr = document.getElementById(
					'webpack-dev-server-client-overlay',
				);

				if (resizeObserverErr) {
					resizeObserverErr.setAttribute('style', 'display: none');
				}

				if (resizeObserverErrDiv) {
					resizeObserverErrDiv.setAttribute('style', 'display: none');
				}
			}
		};
		window.addEventListener('error', ignoreErrors);

		return () => {
			window.removeEventListener('error', ignoreErrors);
		};
	}, []);

	useEffect(() => {
		if (import.meta.env.VITE_PRODUCTION === 'true') {
			// @ts-ignore it does exist - defined in index.html
			// TOOD put a new ID
			// window?.heap?.load?.('116295093');
		}

		dayjs.locale({
			...en,
			weekStart: 1,
		});

		Sentry.setUser({ token: useAccessTokenStore.getState().accessToken ?? 'Unknown' });
	}, []);

	const logo = (
		<img
			alt="logo"
			className="w-[174px] h-[80px]"
			src={logoImg}
		/>
	);

	const params = new URLSearchParams(window.location.search);

	const {
		beforeClose,
		dialog: confirmLogoutDialog,
	} = useConfirmActionDialog({
		DialogComponent: ConfirmLogoutDialog,
		onConfirm:       async () => {
			await logoutRef.current?.();

			useSupergridSettings.getState().resetStore();
		},
		defaultShouldPromptOnClose: true,
	});

	async function onBeforeLogout() {
		beforeClose();
	}

	// we need to memoize it to avoid re-rendering the whole app every time navigate() is called
	const AppComponent = useMemo(() => (
		<AuthProvider
			loginUri={`${import.meta.env.VITE_API_PATH}/auth/token/gn_ga/user/`}
			logoutUri={`${import.meta.env.VITE_API_PATH}/auth/logout/gn_ga/user/`}
			graphQLUri={`${import.meta.env.VITE_API_PATH}/graphql/${NETWORK}`}
			refreshTokenUri={REFRESH_TOKEN_URI}
			onSessionExpired={onSessionExpired}
		>
			{
				({
					isLoggedIn, login, logout, refreshToken,
				}) => {
					logoutRef.current = logout;

					return (
						isLoggedIn
							? (
								<>
									<SpotifyResponse />
									<RedditSideMenu logout={onBeforeLogout} />
									<AppRoutes />
									{/* TODO Change for spotify */}
									<AdBlockModal knowledgeBaseUrl="https://knowledge.wise.blue/wise-blue-for-spotify" />
								</>
							)
							: (
								<LoginBlock
									logo={logo}
									refreshToken={refreshToken}
									login={login}
									certPath={`${import.meta.env.VITE_API_PATH}/cert-email/`}
									navigate={navigate}
									urlParams={urlParams}
								/>
							)
					);
				}
			}
		</AuthProvider>
	), []);

	if (showRequestPasswordReset) {
		return (
			<RequestPasswordResetPage
				logo={logo}
				navigate={navigate}
				onRequest={async (userEmail) => {
					await publicClient?.mutate({
						mutation:  RequestResetPasswordDocument,
						variables: { userEmail },
					});
				}}
			/>
		);
	}

	if (showResetPassword) {
		return (
			<PasswordResetPage
				logo={logo}
				navigate={navigate}
				type={params.get('type')}
				onReset={async (userPassword) => {
					await publicClient?.mutate({
						mutation:  ResetPasswordDocument,
						variables: { userPassword, token: params?.get('token') ?? '' },
					});
				}}
			/>
		);
	}

	return (
		<>
			{confirmLogoutDialog}
			{AppComponent}
		</>
	);
};

export default App;
